import { getLangCode } from "@/app/(site)/_utils/misc"
import { CENTRA_LOCALE_SHIPPING_COUNTRY_CODES } from "@/data/centra-locale-shipping-country-code"
import contentServiceClientOnlyClient from "@/lib/content-service/content-service-only-client-init"
import { mapCentraLocaleToMarketCodeThenToMarketId } from "@/lib/content-service/helpers/mapCentraLocaleToMarketCodeThenToMarketId"
import logger from "@/lib/logger/logger-init"
import { mapCentraLocaleToPriceListId } from "@/maps/map-utils"
import { SiteContext } from "./siteContext"

export interface CategoryEnrichmentResult {
    [sku: string]: {
        item_category?: string
        item_category2?: string
        item_category3?: string
    }
}

function deriveFullSku(itemId: string, itemVariant?: string): string {
    if (itemVariant) {
        return `${itemId}${itemVariant}`
    }

    return itemId
}

/**
 * Checks if tracking items already have category data
 */
function needsCategoryEnrichment(
    trackingItems: Array<{
        item_id: string
        item_variant?: string
        item_category?: string
    }>
): boolean {
    return trackingItems.some(item => !item.item_category)
}

/**
 * Enriches tracking data with category information from Content Service API
 * Returns enriched data that can be used with the normal send function
 */
export async function enrichTrackingDataWithCategories(
    trackingItems: Array<{
        item_id: string
        item_variant?: string
        item_category?: string
    }>,
    siteContext: SiteContext
): Promise<
    Array<{
        item_id: string
        item_variant?: string
        item_category?: string
        item_category2?: string
        item_category3?: string
    }>
> {
    if (trackingItems.length === 0) return trackingItems

    // Check if we need to enrich the data
    if (!needsCategoryEnrichment(trackingItems)) {
        logger.debug("Tracking items already have category data, skipping enrichment")
        return trackingItems
    }

    try {
        // Derive full SKUs from tracking data
        const skus = trackingItems
            .map(item => deriveFullSku(item.item_id, item.item_variant))
            .filter(Boolean) as string[]

        if (skus.length === 0) {
            logger.debug("No valid SKUs found for category enrichment")
            return trackingItems
        }

        // Construct full centraLocale from site_country and site_language
        const countryCode = siteContext.site_country.toLowerCase()
        const languageCode = siteContext.site_language.toLowerCase()

        // Get the centra locale shipping country code for this country
        const centraLocaleShippingCountryCode =
            CENTRA_LOCALE_SHIPPING_COUNTRY_CODES[
                countryCode as keyof typeof CENTRA_LOCALE_SHIPPING_COUNTRY_CODES
            ]

        // If the centra locale shipping country code is empty, it's a EUR currency country
        // For EUR currency countries, use just the language code (e.g., "fr", "de", "en")
        // For other countries, use language-country format (e.g., "en-se", "en-gb")
        const centraLocale =
            centraLocaleShippingCountryCode === ""
                ? languageCode
                : `${languageCode}-${centraLocaleShippingCountryCode}`

        const marketId = mapCentraLocaleToMarketCodeThenToMarketId(centraLocale)
        const pricelistId = mapCentraLocaleToPriceListId(centraLocale) || 4
        const language = getLangCode(siteContext.site_language.toLowerCase()) || "en"

        logger.debug(`Enriching ${skus.length} SKUs with categories`, {
            skus,
            marketId,
            pricelistId,
            language
        })

        const response = await contentServiceClientOnlyClient.product.getCategoryUris({
            skus: skus.join(","),
            marketId,
            pricelistId,
            language
        })

        if (response.success && response.data) {
            // Create a map of full SKU to category data
            const categoryMap: CategoryEnrichmentResult = {}

            Object.entries(response.data as Record<string, string>).forEach(
                ([sku, categoryUri]) => {
                    if (categoryUri) {
                        const uriParts = categoryUri.split("/").filter(Boolean)
                        categoryMap[sku] = {
                            item_category: uriParts[0] || undefined,
                            item_category2: uriParts[1] || undefined,
                            item_category3: uriParts[2] || undefined
                        }
                    }
                }
            )

            // Enrich the tracking items with category data
            const enrichedItems = trackingItems.map(item => {
                const fullSku = deriveFullSku(item.item_id, item.item_variant)
                const categories = fullSku ? categoryMap[fullSku] : null

                return {
                    ...item,
                    ...categories
                }
            })

            logger.debug(
                `Successfully enriched ${Object.keys(categoryMap).length} SKUs with categories`
            )
            return enrichedItems
        } else {
            logger.warn("Failed to get category URIs from Content Service", response)
        }
    } catch (error) {
        // Handle authentication errors gracefully - skip enrichment when API is not available
        if (error instanceof Error && error.message.includes("401")) {
            logger.warn(
                "Content Service API authentication failed - skipping category enrichment"
            )
        } else {
            logger.error("Failed to enrich tracking data with categories", error)
        }
    }

    return trackingItems
}
